import { ICellRendererParams } from 'ag-grid-community';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { ReactComponent as RejectIcon } from 'assets/svgs/reject_red.svg';
import { Button } from 'components/atoms/button';
import { toastService } from 'components/molecules/toast';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { Timesheet, TimesheetStatus, UserLevelSummary } from '../../types';

export const TextLabel = styled.span<{ isApprovedLabel?: boolean }>`
  color: ${({ isApprovedLabel }) =>
    isApprovedLabel ? 'var(--green-50)' : 'var(--red-50)'};
  padding: calc(0.675rem - 3px) var(--spacing-05);
  width: 100%;
  padding: 0;
  padding-right: var(--spacing-02);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-02);
`;

export const LabelWrapper = styled.div`
  display: inline-block;
  height: 100%;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: 0;
  padding-right: var(--spacing-02);
  display: flex;
  justify-content: flex-end;
`;

const AppoveButton = ({ onApprove }) => (
  <StyledButton type="link" onClick={onApprove}>
    <FormattedMessage id="approvals.approve" />
  </StyledButton>
);
const ApprovedLabel = () => {
  return (
    <LabelWrapper>
      <TextLabel isApprovedLabel>
        <ApproveOutline16 />
        <FormattedMessage id="approvals.approved" />
      </TextLabel>
    </LabelWrapper>
  );
};

const RejectedLabel = () => {
  return (
    <LabelWrapper>
      <TextLabel>
        <RejectIcon />
        <FormattedMessage id="timesheets.feature.rejected" />
      </TextLabel>
    </LabelWrapper>
  );
};

const CTAButton = ({ timesheet, onApprove, onViewClick }) => {
  if (timesheet.status === TimesheetStatus.Submitted) {
    return <AppoveButton onApprove={onApprove} />;
  }
  if (timesheet.status === TimesheetStatus.Rejected) {
    return <RejectedLabel />;
  }
  if (timesheet.status === TimesheetStatus.Approved) {
    return <ApprovedLabel />;
  }

  return (
    <StyledButton type="link" onClick={onViewClick}>
      <FormattedMessage id="view" />
    </StyledButton>
  );
};

interface Props extends ICellRendererParams {
  isManagerApproval: boolean;
  isUserAdmin: boolean;
  isWeekFreezed: boolean;
  approveTimesheet: (
    userId: number,
    updatedAt: number,
    projectId?: number,
  ) => Promise<Timesheet>;
  withdrawTimesheet: (userId: number, projectId?: number) => Promise<Timesheet>;
  onUserSelection: (userId: number, projectId?: number) => void;
  refreshData: () => void;
  value: UserLevelSummary;
}
export const ActionCell: FC<Props> = ({
  approveTimesheet,
  isManagerApproval,
  onUserSelection,
  refreshData,
  isWeekFreezed,
  isUserAdmin,
  api,
  value: timesheet,
}) => {
  const translate = useTranslate();

  const onApprove = useCallback(() => {
    (!isManagerApproval
      ? approveTimesheet(
          timesheet.user.userId,
          timesheet.updatedAt,
          timesheet.projectId,
        )
      : approveTimesheet(timesheet.user.userId, timesheet.updatedAt)
    )
      .then(({ updatedAt }) => {
        const row = api?.getRowNode(
          `${timesheet.userId}_${timesheet.projectId}`,
        );

        row?.updateData({
          ...timesheet,
          status: TimesheetStatus.Approved,
          updatedAt,
        });
      })
      .catch((error) => {
        const {
          response: {
            data: { errors },
          },
        } = error;
        const staleDataError = errors.find(({ reason }) =>
          reason.startsWith('Time entries not in sync for given'),
        );

        if (staleDataError) {
          toastService({
            title: translate(
              'timesheets.toast.titles.approval_stale_error' as TranslationKey,
            ),
            message: translate(
              'timesheets.toast.messages.approval_stale_error' as TranslationKey,
              {
                userName: fullName(timesheet.user as User),
              },
            ),
          }).info();
          refreshData();
        }
      });
  }, [
    api,
    approveTimesheet,
    isManagerApproval,
    refreshData,
    timesheet,
    translate,
  ]);

  const onViewClick = useCallback(() => {
    if (timesheet.projectId) {
      onUserSelection(timesheet.user.userId, timesheet.projectId);
    } else {
      onUserSelection(timesheet.user.userId);
    }
  }, [onUserSelection, timesheet.projectId, timesheet.user.userId]);

  if (isUserAdmin) {
    return (
      <CTAButton
        onApprove={onApprove}
        timesheet={timesheet}
        onViewClick={onViewClick}
      />
    );
  }

  if (!isWeekFreezed) {
    return (
      <CTAButton
        onApprove={onApprove}
        timesheet={timesheet}
        onViewClick={onViewClick}
      />
    );
  }

  return (
    <StyledButton type="link" onClick={onViewClick}>
      <FormattedMessage id="view" />
    </StyledButton>
  );
};
